import {
  ChangeEmailVerifyLinkInput,
  ChangeEmailVerifyLinkResultTypenames,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams, useRouter } from 'expo-router';
import useWaitForEmailUpdate from 'features/myVaro/screens/PersonalDetails/ChangeEmail/VerifyNewEmail/hooks/useWaitForEmailUpdate/useWaitForEmailUpdate';
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import ChangeEmailVerifyLink from 'services/graphql/mutations/ChangeEmailVerifyLink';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import { useUserProfile } from 'shared/hooks/useUserProfile';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { useAuth } from 'shared/providers/auth';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';
import { OperationResult } from 'urql';

type ChangeEmailVerifyLinkResponse = {
  changeEmailVerifyLink: {
    __typename: ChangeEmailVerifyLinkResultTypenames;
    message?: string;
  };
};

type ChangeEmailVerifyLinkVariables = {
  input: ChangeEmailVerifyLinkInput;
};

const VerifyEmail = () => {
  const router = useRouter();
  const { code } = useLocalSearchParams<{ code?: string }>();
  const { isLoggedIn } = useAuth();
  const { customerInfo } = useUserProfile();

  // Capture initial email once available - this prevents re-running verification
  // when email changes during polling (urql cache updates on each poll)
  const [initialEmail, setInitialEmail] = useState<string | null>(null);

  const [{ fetching }, executeVerifyLink] = useCancellableMutation<
    ChangeEmailVerifyLinkResponse,
    ChangeEmailVerifyLinkVariables
  >(ChangeEmailVerifyLink);

  const { waitForEmailUpdate, isPolling } = useWaitForEmailUpdate();

  // Capture the email once on first render when data is available
  useEffect(() => {
    if (initialEmail === null && customerInfo?.email?.address) {
      setInitialEmail(customerInfo.email.address);
    }
  }, [initialEmail, customerInfo?.email?.address]);

  useEffect(() => {
    const verifyEmailLink = async () => {
      if (!code) {
        logBreadcrumb({
          level: 'error',
          message: 'No verification code provided in URL parameters',
        });

        router.dismissAll();
        router.replace({
          pathname: '/myvaro/personal-details',
          params: {
            errorKey: 'emailChangeFailed',
          },
        });
        return;
      }

      try {
        const response = (await executeVerifyLink({
          input: { code },
        })) as OperationResult<ChangeEmailVerifyLinkResponse>;

        const changeEmailVerifyResponse = response?.data?.changeEmailVerifyLink;

        if (!changeEmailVerifyResponse) {
          throw new Error('No response from server');
        }

        const errorTypes = [
          ChangeEmailVerifyLinkResultTypenames.ChangeEmailExpiredLink,
          ChangeEmailVerifyLinkResultTypenames.ChangeEmailError,
          ChangeEmailVerifyLinkResultTypenames.ChangeEmailInvalidLink,
          ChangeEmailVerifyLinkResultTypenames.ChangeEmailTooManyLinkAttempts,
        ];

        if (
          changeEmailVerifyResponse.__typename &&
          errorTypes.includes(changeEmailVerifyResponse.__typename)
        ) {
          if ('message' in changeEmailVerifyResponse) {
            logBreadcrumb({
              level: 'error',
              message: `Email verification failed: ${changeEmailVerifyResponse.message}`,
            });

            router.dismissAll();
            router.replace({
              pathname: '/myvaro/personal-details',
              params: {
                errorKey: 'emailChangeFailed',
              },
            });
          }
        } else if (
          changeEmailVerifyResponse.__typename ===
          ChangeEmailVerifyLinkResultTypenames.ChangeEmailVerifyLinkSuccess
        ) {
          const emailUpdated = await waitForEmailUpdate(String(initialEmail));

          router.dismissAll();
          router.replace({
            pathname: '/myvaro/personal-details',
            params: {
              ...(emailUpdated
                ? { msgKey: 'emailChanged' }
                : { errorKey: 'emailChangeFailed' }),
            },
          });
        }
      } catch (error) {
        if (error instanceof Error) {
          logBreadcrumb({
            level: 'error',
            message: `Email verification error: ${error.message}`,
          });

          router.replace({
            pathname: '/myvaro/personal-details',
            params: {
              errorKey: 'emailChangeFailed',
            },
          });
        }
      }
    };

    if (isLoggedIn && initialEmail) {
      void verifyEmailLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, initialEmail, router]);

  return (
    <View style={styles.container}>
      <ScreenTransitionOverlay
        isLoading={fetching || isPolling}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default VerifyEmail;
